import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type JSX,
  type ReactNode,
} from 'react';

import { applyDocsTheme, getStoredDocsTheme, type DocsTheme } from './docs-theme.js';

interface DocsThemeContextValue {
  theme: DocsTheme;
  setTheme: (theme: DocsTheme) => void;
  toggleTheme: () => void;
}

const DocsThemeContext = createContext<DocsThemeContextValue | null>(null);

export interface DocsThemeProviderProps {
  children: ReactNode;
}

/** Provides light/dark theme state for the documentation shell. */
export function DocsThemeProvider({ children }: DocsThemeProviderProps): JSX.Element {
  const [theme, setThemeState] = useState<DocsTheme>(() => getStoredDocsTheme());

  const setTheme = useCallback((next: DocsTheme) => {
    applyDocsTheme(next);
    setThemeState(next);
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((current) => {
      const next = current === 'light' ? 'dark' : 'light';
      applyDocsTheme(next);
      return next;
    });
  }, []);

  const value = useMemo(() => ({ theme, setTheme, toggleTheme }), [theme, setTheme, toggleTheme]);

  return <DocsThemeContext.Provider value={value}>{children}</DocsThemeContext.Provider>;
}

/** Access the current docs theme and toggle helpers. */
export function useDocsTheme(): DocsThemeContextValue {
  const context = useContext(DocsThemeContext);
  if (!context) {
    throw new Error('useDocsTheme must be used within DocsThemeProvider');
  }
  return context;
}
